<template>
  <input
    v-if="type !== 'date'"
    v-model="content"
    :type="type"
    :class="classObject"
    :placeholder="placeholder"
    class="rounded-lg color-border shadow-xs font-normal text-gray-900 focus:outline-none bg-cs-light-gray"
    :maxlength="maxlength"
    @focus="(e) => $emit('focus', e)"
    @keyup.enter="(val) => $emit('onPressEnter', val)"
    @keydown="(e) => $emit('keydown', e)"
    @blur="(e) => $emit('blur', e)"
  />
  <DatePicker
      v-else
      ref="datepicker"
      v-model="content"
      class="bg-cs-light-gray cst-date-range-picker rounded-lg border color-border shadow-xs"
      :class="[classObject, allowMinWidth ? '!w-full' : '']"
      :style="{ 'min-width': allowMinWidth ? '14.5rem !important' : '' }"
      range-separator=" - "
      input-class="bg-transparent border-0 font-normal text-gray-900 hover:bg-gray-50 focus:outline-none cursor-pointer"
      :format="dateFormat"
      :shortcuts="dateOptions.shortcuts"
      :disabled-date="dateOptions.disabledDate"
      type="date"
      :range="range"
      :placeholder="placeholder"
      :popup-class="[datePickerPopupClasses, 'pop-up-date-picker']"
      :confirm="true"
      confirm-text="Apply"
      @clear="() => $emit('clear-date')"
      @open="handleOpen"
      @close="handleClose"
  >
  </DatePicker>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import { useStore } from '@state/base'
const { getters } = useStore()
export default {
  name: 'CstInputFields',
  components: { DatePicker },
  props: {
    value: {
      type: [String, Number, Array],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    placeholder: {
      type: String,
      default: '',
    },
    dateOptions: {
      type: Object,
      default: () => {
        return {
          format: 'YYYY-MM-DD',
          disabledDate: () => {},
          shortcuts: [],
        }
      },
    },
    size: {
      type: String,
      default: 'default',
      validator: function (value) {
        // The value must match one of these strings
        return ['small', 'default', 'large'].indexOf(value) !== -1
      },
    },
    maxlength: {
      type: [String, Number],
      default: 524288,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    datePickerPopupClasses: {
      type: Array,
      default: () => [],
    },
    range: {
      type: Boolean,
      default: true,
    },
    allowMinWidth: {
      type: Boolean,
      default: true,
    },
    enableAutoClose: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['clear-date'],
  data() {
    const dateFormat = getters?.getProfile?.date_format
    return {
      content: this.value,
      dateFormat,
      isOpen: false,
      clickOutsideHandler: null
    }
  },
  computed: {
    classObject: function () {
      return {
        'px-2 py-2.5 text-sm': this.size === 'small',
        'px-3 py-2.5 text-sm': this.size === 'default',
        'px-3 py-3 text-base': this.size === 'large',
      }
    },
  },
  watch: {
    content(value) {
      this.$emit('value', value)
      this.$emit('change', value)
      this.$emit('input', value)
    },
    value(value) {
      this.content = value
    },
  },
  methods: {
    handleOpen() {
      this.isOpen = true
      if (this.enableAutoClose) {
        this.setUpClickOutsideHandler()
      }
    },

    handleClose() {
      this.isOpen = false
      if (this.enableAutoClose) {
        this.removeClickOutsideHandler()
      }
    },

    setUpClickOutsideHandler() {
      // Only set up the handler if auto-close is enabled
      if (!this.enableAutoClose) {
        return;
      }
      
      // Remove any existing handler first
      this.removeClickOutsideHandler()

      // Create a new handler
      this.clickOutsideHandler = (event) => {
        if (this.isOpen && this.enableAutoClose) {
          // Get the datepicker popup element
          const popupElement = document.querySelector('.mx-datepicker-popup')
          const inputElement = this.$el

          // Check if click is outside both the popup and input
          if (popupElement && !popupElement.contains(event.target) && !inputElement.contains(event.target)) {
            // Close the datepicker
            if (this.$refs.datepicker) {
              this.$refs.datepicker.closePopup()
            }
          }
        }
      }

      // Add the click event listener to document
      document.addEventListener('click', this.clickOutsideHandler, true)
    },

    removeClickOutsideHandler() {
      if (this.clickOutsideHandler) {
        document.removeEventListener('click', this.clickOutsideHandler, true)
        this.clickOutsideHandler = null
      }
    }
  },
  beforeDestroy() {
    // Make sure to remove event listener when component is destroyed
    this.removeClickOutsideHandler()
  }
}
</script>

<style lang="scss">
.pop-up-date-picker {
  .mx-calendar-header {
    /* Select direct buttons inside .mx-calendar-header that are NOT inside .mx-calendar-header-label */
    button:not(.mx-calendar-header-label button) {
      /* Apply styles to direct buttons except the ones inside .mx-calendar-header-label */
      display: none;
    }
  }
}

.cst-date-range-picker input {
  width: 80%;
  overflow: hidden;
}

.mx-datepicker-sidebar {
  width: 130px !important;
}

.mx-datepicker-sidebar + .mx-datepicker-content {
  margin-left: 130px !important;
}
</style>
